import { ref, computed } from 'vue'
import { siteContent } from '@/data/content.js'

/**
 * Composable para manejar contenido dinámico del sitio
 * 
 * USO:
 * 
 * <script setup>
 * import { useContent } from '@/composables/useContent'
 * const { hero, navigation, site } = useContent()
 * </script>
 * 
 * <template>
 *   <h1>{{ hero.title.main }}</h1>
 *   <p>{{ hero.description }}</p>
 * </template>
 * 
 * FUTURO CON STRAPI:
 * - Este composable se conectará a la API de Strapi
 * - Los datos vendrán de la base de datos en lugar del archivo local
 * - Se agregará caché y estado de loading
 */

// Estado global del contenido
const content = ref(siteContent)
const isLoading = ref(false)
const error = ref(null)

export const useContent = () => {
  
  // Función para cargar contenido desde Strapi (futuro)
  const loadContent = async (collection = null) => {
    isLoading.value = true
    error.value = null
    
    try {
      // TODO: Implementar cuando se conecte Strapi
      // const response = await $fetch('/api/content', { 
      //   params: collection ? { collection } : {} 
      // })
      // content.value = response.data
      
      // Por ahora simulamos un delay de red
      await new Promise(resolve => setTimeout(resolve, 100))
      
    } catch (err) {
      error.value = err.message
      console.error('Error loading content:', err)
    } finally {
      isLoading.value = false
    }
  }

  // Función para actualizar contenido específico
  const updateContent = (path, newValue) => {
    const keys = path.split('.')
    let current = content.value
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]]
    }
    
    current[keys[keys.length - 1]] = newValue
  }

  // Getters computados para secciones específicas
  const site = computed(() => content.value.site)
  const navigation = computed(() => content.value.navigation)
  const hero = computed(() => content.value.hero)
  const footer = computed(() => content.value.footer)
  const meta = computed(() => content.value.meta)

  // Función para obtener contenido por ID (útil para colecciones)
  const getContentById = (collection, id) => {
    return content.value[collection]?.find?.(item => item.id === id)
  }

  // Función para formatear texto dinámico (interpolación)
  const formatText = (text, variables = {}) => {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key] || match
    })
  }

  // Función para obtener años dinámicos (copyright, etc)
  const getCurrentYear = () => new Date().getFullYear()

  return {
    // Estado
    content,
    isLoading,
    error,
    
    // Métodos
    loadContent,
    updateContent,
    getContentById,
    formatText,
    getCurrentYear,
    
    // Secciones específicas
    site,
    navigation, 
    hero,
    footer,
    meta
  }
}

// Función helper para componentes que necesitan contenido específico
export const useHeroContent = () => {
  const { hero, isLoading, error } = useContent()
  return { hero, isLoading, error }
}

export const useNavigationContent = () => {
  const { navigation, site, isLoading, error } = useContent()
  return { navigation, site, isLoading, error }
}

export const useFooterContent = () => {
  const { footer, site, getCurrentYear, isLoading, error } = useContent()
  return { footer, site, getCurrentYear, isLoading, error }
}

// ✅ NUEVOS COMPOSABLES - Agregar aquí para nuevas secciones
export const useFeaturesContent = () => {
  const { content, isLoading, error } = useContent()
  const features = computed(() => content.value.features)
  return { features, isLoading, error }
}

export const useTestimonialsContent = () => {
  const { content, isLoading, error } = useContent()
  const testimonials = computed(() => content.value.testimonials)
  return { testimonials, isLoading, error }
}
